/** Hub module gate permissions ({module}:view / {module}:admin) synced with WorkOS infra/workos/rbac/. */

export const HUB_MODULES = [
  'customer-care',
  'campaigns',
  'ai-agents',
  'ingestion',
  'finances',
  'analytics',
  'config',
  'settings',
  'users',
  'admin',
  'library',
  'coming-soon',
] as const;

export type HubModule = (typeof HUB_MODULES)[number];

/** Legacy app:* gates retained during WorkOS permission migration. */
export const LEGACY_MODULE_APP: Partial<Record<HubModule, string | readonly string[]>> = {
  'customer-care': 'app:customers:access',
  campaigns: 'app:campaigns:access',
  'ai-agents': ['app:ai:access', 'app:aichat:access'],
  ingestion: 'app:files:access',
  finances: 'app:finances:access',
  analytics: 'app:monitor:access',
  config: 'app:settings:access',
  settings: 'app:settings:access',
  users: 'app:users:access',
  admin: 'app:admin:access',
  library: 'app:library:access',
  'coming-soon': 'app:coming-soon:access',
};

export function moduleViewPermission(module: string): string {
  return `${module}:view`;
}

export function moduleAdminPermission(module: string): string {
  return `${module}:admin`;
}

export function moduleClientViewPermission(module: string): string {
  return `${module}:client-view`;
}

export function moduleClientAdminPermission(module: string): string {
  return `${module}:client-admin`;
}

function legacyModulePermissions(module: string): string[] {
  const legacy = LEGACY_MODULE_APP[module as HubModule];
  if (!legacy) return [];
  return Array.isArray(legacy) ? [...legacy] : [legacy as string];
}

/** True when the user can open a Hub module (ops, client-track, or legacy gate). */
export function hasModuleAccess(hasPermission: (permission: string) => boolean, module: string): boolean {
  const checks = [
    moduleViewPermission(module),
    moduleAdminPermission(module),
    moduleClientViewPermission(module),
    moduleClientAdminPermission(module),
    ...legacyModulePermissions(module),
  ];
  return checks.some(hasPermission);
}

/** True when the user has a module admin gate (new or legacy). */
export function hasModuleAdminAccess(hasPermission: (permission: string) => boolean, module: string): boolean {
  const checks = [moduleAdminPermission(module), ...legacyModulePermissions(module)];
  return checks.some(hasPermission);
}

/** True when the user has a module view gate (new or legacy). */
export function hasModuleViewAccess(hasPermission: (permission: string) => boolean, module: string): boolean {
  const checks = [moduleViewPermission(module), ...legacyModulePermissions(module)];
  return checks.some(hasPermission);
}

/** True when the user has client-track view access to a module. */
export function hasModuleClientViewAccess(hasPermission: (permission: string) => boolean, module: string): boolean {
  return hasPermission(moduleClientViewPermission(module)) || hasPermission(moduleClientAdminPermission(module));
}

/** True when the user has ops-track admin access (not client-track). */
export function hasModuleOpsAdminAccess(hasPermission: (permission: string) => boolean, module: string): boolean {
  return hasPermission(moduleAdminPermission(module));
}
